import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Tooltip,
} from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import Avatar from 'further-ui/components/Avatar';
import { useFormContext } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { AppNumberInput } from 'components/FormElements';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import PageSection from 'components/PageSection';
import { AddCommitmentsToEqualisationFormValues } from './../FormWrapper';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';

const StyledTable = styled(Table)(() => ({
  width: '100%',
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightLight,
  border: '1px solid #ebebec',
  borderRadius: '8px',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.md),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTableHeaderCell = styled(StyledTableCell)(() => ({
  paddingBottom: 0,
  paddingTop: 0,
}));

const StyledAppNumberInput = styled(AppNumberInput, {
  shouldForwardProp: (prop) => prop !== 'isCPAndHasNewTotalCommitmentValue',
})<{ isCPAndHasNewTotalCommitmentValue?: boolean }>(
  ({ theme, isCPAndHasNewTotalCommitmentValue }) => ({
    width: 150,
    ...(isCPAndHasNewTotalCommitmentValue && {
      '& .MuiOutlinedInput-root.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.info.main,
          borderWidth: '2px',
        },
      },
    }),
  }),
);

interface PreviouslyClosedCommitmentsTableProps {
  previouslyClosedCommitments: Array<{
    id: string;
    investorName: string;
    isCapitalContributionRuleOverride?: boolean;
    isCarryPartnerCommitment?: boolean;
    totalCommitmentLcy: number;
    loanCommitmentLcy?: number;
    capitalCommitmentLcy?: number;
    equalisationDate?: CalendarDay | null;
    existingCapitalCommitmentLcy?: number;
    existingLoanCommitmentLcy?: number;
    existingTotalCommitmentLcy?: number;
  }>;
  baseCurrency?: CurrencyCode;
  capitalCommitmentIsEditable: (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
  capitalCommitmentOnChange: (
    index: number,
    value: number,
    loanCommitmentLcy: number,
  ) => void;
  loanCommitmentIsEditable: (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
  loanCommitmentOnChange: (
    index: number,
    value: number,
    capitalCommitmentLcy: number,
  ) => void;
  capitalContributionRuleOverrideOnChange: (
    index: number,
    value: boolean,
    totalCommitmentLcy: number,
    isCarryPartnerCommitment: boolean | undefined,
  ) => void;
  showOverrideColumn: boolean;
  showOverrideCheckbox: (
    isCarryPartnerCommitment: boolean | undefined,
  ) => boolean;
}

const PreviouslyClosedCommitmentsTable: React.FC<
  PreviouslyClosedCommitmentsTableProps
> = ({
  previouslyClosedCommitments,
  baseCurrency,
  capitalCommitmentIsEditable,
  capitalCommitmentOnChange,
  loanCommitmentIsEditable,
  loanCommitmentOnChange,
  capitalContributionRuleOverrideOnChange,
  showOverrideColumn,
  showOverrideCheckbox,
}) => {
  const { getValues, control, watch } =
    useFormContext<AddCommitmentsToEqualisationFormValues>();
  const formCommitments = watch('commitments');

  const isCPAndHasNewTotalCommitmentValue = (index: number) => {
    const commitment = getValues(`commitments.${index}`);
    return !!(
      commitment?.isCarryPartnerCommitment &&
      commitment?.existingTotalCommitmentLcy !== commitment?.totalCommitmentLcy
    );
  };
  return (
    <PageSection title="Previously closed commitments" withSpacing>
      <TableContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Investor name
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Close date
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Total commitment (current)
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Capital contribution (current)
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Loan commitment (current)
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Capital contribution (new)
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Loan commitment (new)
                </Typography>
              </StyledTableHeaderCell>
              <StyledTableHeaderCell>
                <Typography variant="h6" color="text.secondary">
                  Total commitment (new)
                </Typography>
              </StyledTableHeaderCell>
              {showOverrideColumn && (
                <StyledTableHeaderCell>
                  <Typography variant="h6" color="text.secondary">
                    Manual override
                  </Typography>
                </StyledTableHeaderCell>
              )}
            </TableRow>
          </TableHead>
          <StyledTableBody>
            {previouslyClosedCommitments?.map(
              ({
                id,
                investorName,
                isCapitalContributionRuleOverride,
                isCarryPartnerCommitment,
                totalCommitmentLcy,
                loanCommitmentLcy,
                capitalCommitmentLcy,
                equalisationDate,
                existingCapitalCommitmentLcy,
                existingLoanCommitmentLcy,
                existingTotalCommitmentLcy,
              }) => {
                const originalIndex =
                  formCommitments?.findIndex((c) => c.id === id) ?? -1;
                return (
                  <TableRow key={id}>
                    <StyledTableCell>
                      <Row centered spacing="xs">
                        <Avatar text={investorName} size="lg" color="random" />
                        <Typography variant="body1">{investorName}</Typography>
                      </Row>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body1">
                        {equalisationDate?.toLocalDateLabel() ?? ''}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body1">
                        {numberToCurrencyString(existingTotalCommitmentLcy, {
                          currency: baseCurrency,
                        })}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography variant="body1">
                        {numberToCurrencyString(existingCapitalCommitmentLcy, {
                          currency: baseCurrency,
                        })}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body1">
                        {numberToCurrencyString(existingLoanCommitmentLcy, {
                          currency: baseCurrency,
                        })}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Controller
                        control={control}
                        name={`commitments.${originalIndex}.capitalCommitmentLcy`}
                        render={({
                          field: { value },
                          fieldState: { error },
                        }) => (
                          <StyledAppNumberInput
                            value={value ?? undefined}
                            onChange={(newValue: number | null) => {
                              if (newValue === undefined || newValue === null) {
                                return;
                              }
                              capitalCommitmentOnChange(
                                originalIndex,
                                newValue,
                                loanCommitmentLcy ?? 0,
                              );
                            }}
                            min={0}
                            decimals={2}
                            helperText={error?.message?.toString()}
                            error={!!error}
                            currencyStartAdornment
                            currency={baseCurrency}
                            fullWidth={false}
                            disabled={
                              !capitalCommitmentIsEditable(
                                isCapitalContributionRuleOverride ?? false,
                                isCarryPartnerCommitment,
                              )
                            }
                          />
                        )}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Controller
                        control={control}
                        name={`commitments.${originalIndex}.loanCommitmentLcy`}
                        render={({
                          field: { value },
                          fieldState: { error },
                        }) => (
                          <StyledAppNumberInput
                            value={value ?? undefined}
                            onChange={(newValue: number | null) => {
                              if (newValue === undefined || newValue === null) {
                                return;
                              }
                              loanCommitmentOnChange(
                                originalIndex,
                                newValue,
                                capitalCommitmentLcy ?? 0,
                              );
                            }}
                            min={0}
                            decimals={2}
                            helperText={error?.message?.toString()}
                            error={!!error}
                            currencyStartAdornment
                            currency={baseCurrency}
                            fullWidth={false}
                            disabled={
                              !loanCommitmentIsEditable(
                                isCapitalContributionRuleOverride ?? false,
                                isCarryPartnerCommitment,
                              )
                            }
                          />
                        )}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Controller
                        control={control}
                        name={`commitments.${originalIndex}.totalCommitmentLcy`}
                        render={({
                          field: { value },
                          fieldState: { error },
                        }) => (
                          <Tooltip
                            title={
                              error?.message ||
                              (isCPAndHasNewTotalCommitmentValue(
                                originalIndex,
                              ) &&
                                "The carried interest partner's total commitment is planned to change in this close. If this is unintended, update the capital contribution or loan commitment so the new total matches the existing total.")
                            }
                            arrow
                            placement="top"
                          >
                            <span>
                              <StyledAppNumberInput
                                isCPAndHasNewTotalCommitmentValue={isCPAndHasNewTotalCommitmentValue(
                                  originalIndex,
                                )}
                                value={value ?? undefined}
                                onChange={() => {}}
                                min={0}
                                decimals={2}
                                error={!!error}
                                currencyStartAdornment
                                currency={baseCurrency}
                                fullWidth={false}
                                disabled={true}
                              />
                            </span>
                          </Tooltip>
                        )}
                      />
                    </StyledTableCell>

                    {showOverrideColumn && (
                      <StyledTableCell>
                        {showOverrideCheckbox(isCarryPartnerCommitment) && (
                          <CheckboxCell
                            control={control}
                            name={`commitments.${originalIndex}.isCapitalContributionRuleOverride`}
                            onChange={(value) => {
                              capitalContributionRuleOverrideOnChange(
                                originalIndex,
                                value,
                                totalCommitmentLcy,
                                isCarryPartnerCommitment,
                              );
                            }}
                          />
                        )}
                      </StyledTableCell>
                    )}
                  </TableRow>
                );
              },
            )}
          </StyledTableBody>
        </StyledTable>
      </TableContainer>
    </PageSection>
  );
};

export default PreviouslyClosedCommitmentsTable;
